import React, { useRef, useState } from "react";
import moment from "moment";
import { nanoid } from "nanoid";
import constants from "../constants";
import Avatar from "@mui/material/Avatar";
import EditIconSvg from "../Svg/EditIconSvg";
import { useContext, useEffect } from "react";
import CloseSvgIcon from "../Svg/CloseSvgIcon";
import RightSvgIcon from "../Svg/RightSvgIcon";
import DeleteIconSvg from "../Svg/DeleteIconSvg";
import AlertContext from "../context/AlertContext";
import CommentsHooks from "../Hooks/CommentsHooks";
import CodingHeaderHooks from "../Hooks/CodingPage/CodingHeaderHooks";
import useS3FileManagement from "../Hooks/useS3FileManagement";
import Loading from "./Notification-Loading/Loading/Loading";
import useAxiosPrivate from "../Hooks/useAxiosPrivate";
import CommentsComponent from "./Comments/Comments";
import { getDataFromStorage } from "../util";

export default function Comments() {
  const Alert = useContext(AlertContext);
  const { toggleDrawer } = CommentsHooks();

  const { getLearnerId } = CodingHeaderHooks();
  const { getPreSignedUrl } = useS3FileManagement();
  const [comments, setComments] = useState([]);
  const axios = useAxiosPrivate();

  //for editing
  const inputRef = useRef(null);
  const [editedComment, setEditedComment] = useState("");
  const [cursorPosition, setCursorPosition] = useState(0);
  const [editingCommentIndex, setEditingCommentIndex] = useState(null);

  useEffect(() => {
    const getComments = async () => {
      try {
        Alert.setIsLoaded(true);
        let comments;
        if (Alert.showBreadCrumbsData.learnerId) {
          const response = await axios({
            method: "get",
            url: `node/admin/test/comments?libid=${Alert.showBreadCrumbsData.questionId /* Alert.libId */}&testid=${
              Alert.showBreadCrumbsData.type === constants.libTypeIds.MCQ ||
              Alert.showBreadCrumbsData.type === constants.libTypeIds.DESCRIPTIVE ||
              Alert.showBreadCrumbsData.type === constants.libTypeIds.CODING_SNIPPET
                ? Alert.showBreadCrumbsData.testId
                : Alert.showBreadCrumbsData.type ===
                  constants.libTypeIds.ASSIGNMENT
                ? "undefined"
                : Alert.showBreadCrumbsData.testId
            }&learnerid=${Alert.showBreadCrumbsData.learnerId}&contentType=${
              Alert.showBreadCrumbsData.type
            }`,
            headers: {
              "Content-Type": "application/json",
            },
          });
          comments = response?.data ?? [];
        } else {
          comments = JSON.parse(JSON.stringify(Alert.commentsData ?? "[]"));
        }
        for (let i = 0; i < comments.length; i++) {
          const attachments =
            typeof comments[i].commentAttachment === "string"
              ? JSON.parse(comments[i].commentAttachment)
              : comments[i].commentAttachment;
          if (attachments) {
            for (let attachment of attachments) {
              if (!attachment.link.startsWith("http")) {
                attachment.key = attachment.link;
              }
              if (process.env.REACT_APP_DEPLOYMENT === "LOCAL")
                attachment.link =
                  process.env.REACT_APP_DEPLOYMENT + attachment.link;
              else {
                let res = await getPreSignedUrl(
                  attachment.key,
                  undefined,
                  attachment.link.split(".").pop(),
                  "GET"
                );
                attachment.link = res.url;
              }
            }
            comments[i].commentAttachment = attachments;
          }
        }

        setComments(comments);
      } catch (error) {
        console.error("error", error);
      } finally {
        Alert.setIsLoaded(false);
      }
    };
    getComments();
  }, []);

  useEffect(() => {
    if (editingCommentIndex !== null) {
      focusInput();
    }

    if (inputRef.current !== null && editingCommentIndex !== null) {
      if (cursorPosition === editedComment.length) {
        inputRef.current.setSelectionRange(
          editedComment.length,
          editedComment.length
        );
      } else {
        inputRef.current.setSelectionRange(cursorPosition, cursorPosition);
      }
    }
  }, [editingCommentIndex, editedComment]);

  const deleteComment = async (index) => {
    let temp = [...comments];

    temp.splice(index, 1);
    setComments(temp);

    let picType = [];
    let form = new FormData();

    form.append("isdelete", true);
    form.append("contentType", picType);
    form.append("id", comments[index].id);
    form.append("libtype", Alert.showBreadCrumbsData.type);
    form.append("creatorid", getDataFromStorage("learnerid"));
    form.append("learnerid", Alert.showBreadCrumbsData.learnerId);

    if (Alert.showBreadCrumbsData.type === constants.libTypeIds.ASSIGNMENT) {
      form.append("questionid", Alert.showBreadCrumbsData.testId);
    } else if (
      Alert.showBreadCrumbsData.type === constants.libTypeIds.MCQ ||
      Alert.showBreadCrumbsData.type === constants.libTypeIds.DESCRIPTIVE ||
      Alert.showBreadCrumbsData.type === constants.libTypeIds.CODING_SNIPPET
    ) {
      form.append("testid", Alert.showBreadCrumbsData.testId);
    } else {
      form.append("testid", Alert.showBreadCrumbsData.testId);
      form.append("questionid", Alert.showBreadCrumbsData.questionId);
    }

    if (Alert.commentAttachment.length > 0) {
      for (let i = 0; i < Alert.commentAttachment.length; i++) {
        form.append(
          Alert.commentAttachment[i].file.name,
          Alert.commentAttachment[i].file
        );
        picType.push(Alert.commentAttachment[i].file.name.replace(/^.*\./, ""));
      }
    }

    await axios({
      data: form,
      method: "post",
      url: "node/admin/test/comment",
      headers: {
        "Content-type": "multipart/form-data",
      },
    });

    // setEditedComment("");
    // setEditingCommentIndex(null);
  };

  const saveEditedComment = async () => {
    const updatedComments = [...comments];

    updatedComments[editingCommentIndex].comments = editedComment;
    updatedComments[editingCommentIndex].date = moment().format(
      "YYYY-MM-DD HH:mm:ss"
    );

    // Perform any necessary API call to update the comment on the backend

    let picType = [];
    let form = new FormData();

    form.append("isupdate", true);
    form.append("contentType", picType);
    form.append("lpid", Alert.showBreadCrumbsData.lpId);
    form.append("libtype", Alert.showBreadCrumbsData.type);
    form.append("learnerid", Alert.showBreadCrumbsData.learnerId);
    form.append("creatorid", getDataFromStorage("learnerid"));
    form.append("id", updatedComments[editingCommentIndex].id);
    form.append("comments", updatedComments[editingCommentIndex].comments);

    if (Alert.showBreadCrumbsData.type === constants.libTypeIds.ASSIGNMENT) {
      form.append("questionid", Alert.showBreadCrumbsData.testId);
    } else if (
      Alert.showBreadCrumbsData.type === constants.libTypeIds.MCQ ||
      Alert.showBreadCrumbsData.type === constants.libTypeIds.DESCRIPTIVE ||
      Alert.showBreadCrumbsData.type === constants.libTypeIds.CODING_SNIPPET
    ) {
      form.append("testid", Alert.showBreadCrumbsData.testId);
    } else {
      form.append("testid", Alert.showBreadCrumbsData.testId);
      form.append("questionid", Alert.showBreadCrumbsData.questionId);
    }

    if (Alert.commentAttachment.length > 0) {
      for (let i = 0; i < Alert.commentAttachment.length; i++) {
        form.append(
          Alert.commentAttachment[i].file.name,
          Alert.commentAttachment[i].file
        );
        picType.push(Alert.commentAttachment[i].file.name.replace(/^.*\./, ""));
      }
    }

    await axios({
      data: form,
      method: "post",
      url: "node/admin/test/comment",
      headers: {
        "Content-type": "multipart/form-data",
      },
    });

    // Clear editing state and setcursor position

    setCursorPosition(0);
    setEditedComment("");
    setEditingCommentIndex(null);
  };
  const ignoreEditedComment = () => {
    setCursorPosition(0);
    setEditingCommentIndex(null);
    setEditedComment("");
  };

  const handleCommentChange = (e) => {
    e.preventDefault();

    const { value, selectionStart } = e.target;

    setEditedComment(value);

    // Update the cursor position

    setCursorPosition(selectionStart);
    focusInput();
  };
  const editComment = (index) => {
    setEditingCommentIndex(index);
    setEditedComment(comments[index].comments);
    setCursorPosition(comments[index].comments.length);
    focusInput();
  };

  const focusInput = () => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  return (
    <>
      {Alert.isLoaded && <Loading />}

      <div className="commentsMainContainer">
        <div className="commentsHeader">
          <div className="commentsHeaderLabel">Comments</div>
          <CloseSvgIcon
            component="comments"
            onClick={toggleDrawer("right", false)}
          />
        </div>

        <div
          className={`${
            Alert.isAdmin ? "commentsBody" : "commentsBodyNotAdmin"
          }`}
        >
          {comments?.map(
            (commentdata, index) =>
              commentdata.comments !== null && (
                <div key={nanoid()} className="commentsBodyDiv">
                  <Avatar
                    alt={"Icon"}
                    src={"Profile"}
                    className="commentsBodyDivProfile"
                  />
                  <div className="commentsBodyDivHeader">
                    <div className="commentsBodyDivHeaderInnerDiv">
                      <div className="commentsBodyDivHeaderInnerDivName">
                        {commentdata.createdby}
                      </div>
                      {Alert.isAdmin &&
                        (editingCommentIndex === index ? (
                          <div className="commentsBodyDivEditIcon">
                            <RightSvgIcon onClick={saveEditedComment} />
                            <CloseSvgIcon
                              component="commentsEdit"
                              onClick={ignoreEditedComment}
                            />
                          </div>
                        ) : (
                          <div className="commentsBodyDivEditIconOuterDiv">
                            <EditIconSvg onClick={() => editComment(index)} />
                            <DeleteIconSvg
                              onClick={() => {
                                deleteComment(index);
                              }}
                            />
                          </div>
                        ))}
                    </div>
                    {editingCommentIndex === null && (
                      <div className="commentsBodyDivHeaderTime">
                        {moment(commentdata.date).fromNow()}
                      </div>
                    )}
                    <div className="commentsBodyCommentsDiv">
                      {editingCommentIndex === index ? (
                        <textarea
                          rows={3}
                          type="text"
                          ref={inputRef}
                          value={editedComment}
                          onChange={handleCommentChange}
                          placeholder="Write message for your trainee"
                          className="test-edited-Comment-admin-textarea"
                        />
                      ) : (
                        <span className="commentsBodyCommentsDivSpan">
                          {commentdata.comments}
                        </span>
                      )}
                    </div>
                    {(Alert.testData.testtype ===
                      constants.libTypeIds.ASSIGNMENT ||
                      Alert.testData.libtype ===
                        constants.libTypeIds.ASSIGNMENT) && (
                      <div>
                        {(typeof commentdata.commentAttachment === "string"
                          ? JSON.parse(commentdata.commentAttachment)
                          : commentdata.commentAttachment
                        )?.map((attachment) => (
                          <img
                            title="image"
                            key={nanoid()}
                            onClick={(event) => {
                              event.target.requestFullscreen();
                            }}
                            className="admin-view-comments-section-comment-div-comment-givenattachment"
                            src={`${
                              attachment?.hasOwnProperty("file")
                                ? String(process.env.REACT_APP_NODE_API) +
                                  "library/submission/" +
                                  getLearnerId() +
                                  "/" +
                                  attachment.file.name
                                : attachment.link
                            }`}
                            alt={
                              attachment?.hasOwnProperty("file")
                                ? attachment.file.name
                                : attachment.name
                            }
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )
          )}
        </div>
        <CommentsComponent
          title="Add Comments"
          isAddAttachment={true}
          hasButton={true}
          comments={comments}
          setComments={setComments}
        />
      </div>
    </>
  );
}
